"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import {
  detectEasterEgg,
  themeEasterEgg,
  type EasterEgg,
} from "@/lib/easter-eggs";

const EASTER_EGG_CONTENT: Record<
  EasterEgg,
  { icon: string; message: string }
> = {
  awoo: { icon: "fa-moon", message: "The yard heard you. Awoooo." },
  boop: { icon: "fa-hand-pointer", message: "Boop received. Nose status: excellent." },
  "cathode-signal": { icon: "fa-terminal", message: "CATHODE LINK // SIGNAL LOCKED." },
  "collar-check": { icon: "fa-tag", message: "Collar check complete. Fit: perfect." },
  "good-pup": { icon: "fa-paw", message: "Good pup protocol unlocked." },
  "howl-signal": { icon: "fa-moon", message: "The moon answers. Awoooo." },
  "steel-yard-signal": { icon: "fa-hammer", message: "SHIFT BELL // WORKSHOP OPEN." },
  treat: { icon: "fa-bone", message: "Treat dispensed. Good behaviour noted." },
};

/**
 * Client runtime ported from the Laravel app's `resources/js/app.js`: Alpine.js,
 * the shared `sidebar` / `overlay` stores, the
 * `modalPanel` focus-trap, and the `window.pawContrastRatio` helper.
 *
 * Not ported yet: the `designPicker`, `stickerBoard` and `sortableList` Alpine
 * components — they're bound to Laravel routes / CSRF and have nothing to attach
 * to until the Design editors are ported. Add them alongside those pages.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    Alpine?: any;
    pawContrastRatio?: (hexA: string, hexB: string) => number;
    __pawRuntimeStarted?: boolean;
  }
}

export function PawRuntime() {
  const pathname = usePathname();
  const [easterEgg, setEasterEgg] = useState<EasterEgg | null>(null);

  useEffect(() => {
    const keys: string[] = [];
    let logoClicks = 0;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        (target instanceof HTMLElement &&
          (target.matches("input, textarea, select") || target.isContentEditable))
      ) {
        return;
      }

      keys.push(event.key.toLowerCase());
      if (keys.length > 10) keys.shift();

      const found = detectEasterEgg(keys);
      if (found) {
        keys.length = 0;
        setEasterEgg(found);
      }
    };
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      const logo =
        target instanceof Element ? target.closest('img[src$="/collr-logo.png"]') : null;
      if (logo) {
        logoClicks++;
        if (logoClicks === 5) {
          logoClicks = 0;
          setEasterEgg("collar-check");
        }
        return;
      }

      if (
        event.detail !== 3 ||
        !(target instanceof Element) ||
        target.closest("a, button, input, select, textarea")
      ) return;

      const frame = target.closest<HTMLElement>("[data-profile-theme]");
      const found = frame ? themeEasterEgg(frame.dataset.profileTheme ?? "") : null;
      if (found) setEasterEgg(found);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("click", onClick);
    };
  }, []);

  useEffect(() => {
    if (!easterEgg) return;
    const timer = window.setTimeout(() => setEasterEgg(null), 3600);
    return () => window.clearTimeout(timer);
  }, [easterEgg]);

  useEffect(() => {
    let title = document.title;
    const onVisibilityChange = () => {
      if (document.hidden) {
        title = document.title;
        document.title = "Gone walkies? · Collr";
      } else {
        document.title = title;
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (document.title === "Gone walkies? · Collr") document.title = title;
    };
  }, [pathname]);

  useEffect(() => {
    if (window.__pawRuntimeStarted) {
      return;
    }
    window.__pawRuntimeStarted = true;

    // WCAG 2.1 contrast ratio (1..21) between two "#rrggbb" colours. Mirrors
    // App\Support\Colors\TextContrast on the server.
    window.pawContrastRatio = (hexA: string, hexB: string) => {
      const luminance = (hex: string) => {
        const value = String(hex).replace("#", "");
        if (value.length !== 6) {
          return 0;
        }
        const channels = [0, 2, 4].map((i) => {
          const channel = parseInt(value.slice(i, i + 2), 16) / 255;
          return channel <= 0.03928
            ? channel / 12.92
            : ((channel + 0.055) / 1.055) ** 2.4;
        });
        return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
      };

      const a = luminance(hexA);
      const b = luminance(hexB);
      return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
    };

    void (async () => {
      const { default: Alpine } = await import("alpinejs");

      document.addEventListener("alpine:init", () => {
        Alpine.store("sidebar", {
          isExpanded:
            typeof window !== "undefined" && window.innerWidth >= 1280,
          isMobileOpen: false,
          isHovered: false,
          isOpen() {
            return this.isExpanded || this.isHovered || this.isMobileOpen;
          },
          toggleExpanded() {
            this.isExpanded = !this.isExpanded;
            this.isMobileOpen = false;
          },
          toggleMobileOpen() {
            this.isMobileOpen = !this.isMobileOpen;
          },
          setMobileOpen(value: boolean) {
            this.isMobileOpen = value;
          },
          setHovered(value: boolean) {
            if (window.innerWidth >= 1280 && !this.isExpanded) {
              this.isHovered = value;
            }
          },
        });

        // Shared scroll-lock for full-screen overlays. Depth-counted so nested
        // or sibling overlays don't unlock the page while another is open.
        Alpine.store("overlay", {
          depth: 0,
          lock() {
            this.depth++;
            document.documentElement.style.overflow = "hidden";
            document.body.style.overflow = "hidden";
          },
          unlock() {
            this.depth = Math.max(0, this.depth - 1);
            if (this.depth === 0) {
              document.documentElement.style.overflow = "";
              document.body.style.overflow = "";
            }
          },
        });

        // Full-screen modal panel: lock page scroll while open, move focus in,
        // and keep Tab/Shift+Tab cycling inside it.
        Alpine.data("modalPanel", () => ({
          _open: false,
          activate(this: any) {
            if (this._open) return;
            this._open = true;
            Alpine.store("overlay").lock();
            this.$nextTick(() => {
              if (!this.$el.contains(document.activeElement)) {
                this.$el.focus();
              }
            });
          },
          deactivate(this: any) {
            if (!this._open) return;
            this._open = false;
            Alpine.store("overlay").unlock();
          },
          trapTab(this: any, event: KeyboardEvent) {
            const candidates = this.$el.querySelectorAll(
              'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])',
            );
            const focusable = Array.from(candidates).filter(
              (el) => (el as HTMLElement).offsetParent !== null,
            ) as HTMLElement[];
            if (focusable.length === 0) {
              event.preventDefault();
              this.$el.focus();
              return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;
            if (event.shiftKey && (active === first || active === this.$el)) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && active === last) {
              event.preventDefault();
              first.focus();
            }
          },
        }));
      });

      window.Alpine = Alpine;
      Alpine.start();
    })();
  }, []);

  const content = easterEgg ? EASTER_EGG_CONTENT[easterEgg] : null;

  return content ? (
    <div
      role="status"
      aria-atomic="true"
      className="paw-easter-egg fixed right-4 bottom-4 z-[100] w-[min(22rem,calc(100vw-2rem))] border border-(--paw-signal) bg-(--paw-panel) shadow-[4px_4px_0_var(--paw-signal-deep)] sm:right-6 sm:bottom-6"
    >
      <div className="paw-hazard-sm" aria-hidden="true" />
      <div className="flex items-center gap-3 px-4 py-3.5">
        <i
          className={`fa-solid ${content.icon} text-lg text-(--paw-signal)`}
          aria-hidden="true"
        />
        <div>
          <div className="font-paw-mono text-[10px] tracking-[0.18em] text-(--paw-signal)">
            HIDDEN SIGNAL FOUND
          </div>
          <div className="mt-1 text-sm font-semibold text-(--paw-paper)">
            {content.message}
          </div>
        </div>
      </div>
    </div>
  ) : null;
}
